import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { my_routes } from "../router/all_routes";
import { toast } from "sonner";
import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import { SubmitHandler, useForm } from "react-hook-form";
import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { getAllDepartment, getUser, updateUser } from "../../api/users";
import {
  Action,
  buildPermissionContext,
  Resource,
} from "../../core/common/autorisations/permissionsMap";

// Schema de notre formulaire de modification de compte
const schema = z.object({
  lastname: z.string().nonempty("Veuillez insérer un nom"),
  firstname: z.string().nonempty("Veuillez insérer un prénom"),
  sexe: z.string().nonempty("Veuillez sélectionner le sexe"),
  email: z.string().email("Veuillez fournir une adresse email valide"),
  number: z
    .string()
    .regex(
      /^(\+223)?[456789]\d{7}$/,
      "Numéro de téléphone invalide. Ex: +22370123456 ou 70123456",
    )
    .nonempty("Veuillez insérer un numéro de téléphone"),
  id_department: z.string().nonempty("Le département est requis"),
  jobFunction: z.string().nonempty("Veuillez insérer une fonction"),
  birthday: z.string({ message: "Veuillez insérer une date de naissance" }),
  adresse: z.string().nonempty("Veuillez insérer une adresse"),
  statut: z.enum(["true", "false"], {
    required_error: "Veuillez sélectionner un statut",
  }),
});
type FormFields = z.infer<typeof schema>;

interface Department {
  id: string;
  label: string;
}

const EditUser = () => {
  const navigate = useNavigate();
  const { id } = useParams(); // On récupère l'id de l'URL
  const routes = my_routes;

  const [user, setUser] = useState([]);
  const [departments, setDepartments] = useState<Department[]>([]);

  //Initialisation de zod sur notre formulaire de modification de compte
  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormFields>({
    resolver: zodResolver(schema),
  });

  //Traitement après validation du formulaire de modification de compte
  const onSubmit: SubmitHandler<FormFields> = async (data) => {
    try {
      //On convertit le champ statut qui n'arrive pas à être envoyé en string
      const userData = {
        ...data,
        statut: data.statut === "true", // conversion string → boolean
      };

      //Envoi des datas pour modification vers le backend
      // console.log(userData)
      await updateUser(userData, id);

      //On réinitialise les champs du formulaire
      reset();

      //On rédirige vers la liste des utilisateurs
      navigate("/all-user", {
        state: { successMessage: "Utilisateur créé avec succès." },
      });
    } catch (error: any) {
      // Si erreur
      // console.log(error);
      // Si erreur (Toast Error)
      toast.error(
        error.response?.data?.message ||
          error.response?.data?.error ||
          error.message ||
          "Une erreur est survenue, veuillez réessayer plus tard.",
      );
    }
  };

  //On recupère les infos du User à modifier
  useEffect(() => {
    const fetchUser = async () => {
      try {
        const response = await getUser(id);
        if (!response) {
          // Si aucun utilisateur trouvé
          throw new Error("Utilisateur introuvable");
        }

        reset({
          // Si utilisateur trouvé on rempli les champs du formulaire
          firstname: response.data.firstname,
          lastname: response.data.lastname,
          sexe: response.data.sexe,
          email: response.data.email,
          number: response.data.number,
          id_department: response.data.id_department,
          jobFunction: response.data.jobFunction,
          birthday: response.data.birthday,
          adresse: response.data.adresse,
          statut: response.data.statut.toString(), // On convertit le statut en string pour le formulaire
        });
      } catch (error: any) {
        // Si erreur (Toast Error)
        toast.error(
        error.response?.data?.message ||
        error.response?.data?.error ||
        error.message ||
        "Une erreur s'est produit, Réessayer plus tard",
      );
      }
    };

    getAllDepartment(buildPermissionContext(Resource.USER, Action.UPDATE))
      .then((departments) => {
        setDepartments(departments.data);
        fetchUser();
      })
      .catch((error) => {
        toast.error("Une erreur s'est produite, réessayez plus tard.");
      });
  }, [id, reset]);

  return (
    <>
      <div className="page-wrapper">
        <div className="content">
          {/* Page Header */}
          <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
            <div className="my-auto mb-2">
              <h3 className="page-title mb-1">Modifier un Compte Personnel</h3>
              <nav>
                <ol className="breadcrumb mb-0">
                  <li className="breadcrumb-item">
                    <Link to={routes.allUser}>Compte</Link>
                  </li>
                  <li className="breadcrumb-item active" aria-current="page">
                    Modification
                  </li>
                </ol>
              </nav>
            </div>
          </div>
          {/* /Page Header */}

          {/* Message d'erreur serveur */}
          {errors.root && (
            <div className="alert alert-danger rounded-pill alert-dismissible fade show">
              {errors.root.message}
              <button
                type="button"
                className="btn-close"
                data-bs-dismiss="alert"
                aria-label="Close"
              >
                <i className="fas fa-xmark" />
              </button>
            </div>
          )}

          <div className="row">
            <div className="col-md-12">
              <form
                className="needs-validation"
                onSubmit={handleSubmit(onSubmit)}
              >
                <>
                  {/* Personal Information */}
                  <div className="card">
                    <div className="card-header bg-light">
                      <div className="d-flex align-items-center">
                        <span className="bg-white avatar avatar-sm me-2 text-gray-7 flex-shrink-0">
                          <i className="ti ti-info-square-rounded fs-16" />
                        </span>
                        <h4 className="text-dark">
                          {" "}
                          Informations Personnelles
                        </h4>
                      </div>
                    </div>
                    <div className="card-body pb-1">
                      <div className="row row-cols-xxl-5 row-cols-md-6">
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label
                              className="form-label"
                              htmlFor="validationServer01"
                            >
                              {" "}
                              Nom
                            </label>
                            <input
                              {...register("lastname")}
                              id="validationServer01"
                              type="text"
                              className={
                                errors.lastname
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.lastname && (
                              <div className="invalid-feedback">
                                {errors.lastname.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label"> Prénom</label>
                            <input
                              {...register("firstname")}
                              type="text"
                              className={
                                errors.firstname
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.firstname && (
                              <div className="invalid-feedback">
                                {errors.firstname.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Sexe</label>
                            <select
                              {...register("sexe")}
                              aria-label="Default select example"
                              className={
                                errors.sexe
                                  ? "form-select is-invalid"
                                  : "form-select"
                              }
                            >
                              <option value={""}>Sélectionner</option>
                              <option value={"Masculin"}>Homme</option>
                              <option value={"Feminin"}>Femme</option>
                            </select>
                            {errors.sexe && (
                              <div className="invalid-feedback">
                                {errors.sexe.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Addresse Email</label>
                            <input
                              {...register("email")}
                              type="text"
                              className={
                                errors.email
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.email && (
                              <div className="invalid-feedback">
                                {errors.email.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Contact Mobile/Fixe
                            </label>
                            <input
                              {...register("number")}
                              type="text"
                              className={
                                errors.number
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.number && (
                              <div className="invalid-feedback">
                                {errors.number.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              Date de Naissance
                            </label>
                            <div className="input-icon position-relative">
                              <input
                                type="date"
                                {...register("birthday")}
                                placeholder="DD-MM-YYYY"
                                className={
                                  errors.birthday
                                    ? "form-control datetimepicker is-invalid"
                                    : "form-control datetimepicker"
                                }
                              />
                              {errors.birthday && (
                                <div className="invalid-feedback">
                                  {errors.birthday.message}
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Département</label>
                            <select
                              {...register("id_department")}
                              aria-label="Default select example"
                              className={
                                errors.id_department
                                  ? "form-select is-invalid"
                                  : "form-select"
                              }
                            >
                              <option value={""}>Sélectionner</option>
                              {departments.map((department) => (
                                <option
                                  key={department.id}
                                  value={department.id}
                                >
                                  {department.label}
                                </option>
                              ))}
                            </select>
                            {errors.id_department && (
                              <div className="invalid-feedback">
                                {errors.id_department.message}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="col-xxl-4 col-xl-4 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">
                              {" "}
                              Fonction (Poste)
                            </label>
                            <input
                              {...register("jobFunction")}
                              type="text"
                              className={
                                errors.jobFunction
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.jobFunction && (
                              <div className="invalid-feedback">
                                {errors.jobFunction.message}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xxl-12 col-md-12 col-xl-12">
                          <div className="mb-3">
                            <label className="form-label">
                              Adresse Physique
                            </label>
                            <textarea
                              {...register("adresse")}
                              placeholder="Exemple : Badalabougou, Commune 5, Rue 300 Porte 218"
                              rows={4}
                              className={
                                errors.adresse
                                  ? "form-control is-invalid"
                                  : "form-control"
                              }
                            />
                            {errors.adresse && (
                              <div className="invalid-feedback">
                                {errors.adresse.message}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="col-xxl-3 col-xl-3 col-md-6">
                          <div className="mb-3">
                            <label className="form-label">Status</label>
                            <select
                              {...register("statut")}
                              aria-label="Default select example"
                              className={
                                errors.statut
                                  ? "form-select is-invalid"
                                  : "form-select"
                              }
                            >
                              <option value={""}>Sélectionner</option>
                              <option value={"true"}>Actif</option>
                              <option value={"false"}>Inactif</option>
                            </select>
                            {errors.statut && (
                              <div className="invalid-feedback">
                                {errors.statut.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                  {/* /Personal Information */}
                </>

                <div className="text-end">
                  <button type="button" className="btn btn-light me-3">
                    <Link to={routes.allUser}>Annuler</Link>
                  </button>

                  {isSubmitting ? (
                    <button
                      className="btn btn-primary-light"
                      type="button"
                      disabled={false}
                    >
                      <span
                        className="spinner-border spinner-border-sm align-middle me-1"
                        role="status"
                        aria-hidden="true"
                      />
                      Chargement...
                    </button>
                  ) : (
                    <button
                      disabled={isSubmitting}
                      type="submit"
                      className="btn btn-primary"
                    >
                      Enregistrer
                    </button>
                  )}
                </div>
              </form>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default EditUser;
